import React from 'react';
import { useAuth } from '../context/AuthContext';
import { usePresence } from '../context/PresenceContext';
import { useNavigate } from 'react-router-dom';
import { FolderOpen, Mail, MessageSquare, Video, User, Lock, Shield, FileText, Settings as SettingsIcon, Sparkles, Activity, ClipboardList, Calendar, Briefcase, UserCircle, Receipt, DollarSign, Mic, Phone, Filter, UserPlus, TrendingUp, Clock, CheckCircle, AlertCircle, BookOpen, Ticket, Search, Eye } from 'lucide-react';
import { getDashboardStats as getMailStats } from '../services/mailService';
import api from '../services/api';

import SpaceBackground from '../components/layout/SpaceBackground';
import WeatherWidget from '../components/WeatherWidget';
import TodaysEventsWidget from '../components/TodaysEventsWidget';
import TriageWidget from '../components/TriageWidget';
import BillableHoursWidget from '../components/billable/BillableHoursWidget';
import ApprovalWidget from '../components/ApprovalWidget';
import CallbacksCard from '../components/CallbacksCard';
import WhileYouWereGoneWidget from '../components/WhileYouWereGoneWidget.jsx';
import PlanchetteIntelWidget from '../components/PlanchetteIntelWidget';
import EserviceFilingsWidget from "../components/EserviceFilingsWidget";
import AlertCenter from "../components/AlertCenter";
import MorningBriefing from "../components/MorningBriefing";
import SupportTicketsWidget from "../components/SupportTicketsWidget";

const Dashboard = () => {
  const { user, isAdmin, isStaff, isAttorney, isFirmMember } = useAuth();
  const { unreadSummary, emailUnread } = usePresence();
  const sigilUnreadCount = unreadSummary?.total_unread || 0;
  const navigate = useNavigate();
  const [mailUnreadCount, setMailUnreadCount] = React.useState(0);
  const [quickStats, setQuickStats] = React.useState({
    openCases: 0,
    pendingTasks: 0,
    overdueTasks: 0,
    overdueInvoices: 0,
    loading: true
  });
  const [draggedModule, setDraggedModule] = React.useState(null);
  const [moduleOrder, setModuleOrder] = React.useState(() => {
    const saved = localStorage.getItem('dashboardModuleOrder');
    return saved ? JSON.parse(saved) : null;
  });

  React.useEffect(() => {
    const loadMailStats = async () => {
      try {
        const stats = await getMailStats();
        setMailUnreadCount(stats?.overview?.unread || 0);
      } catch (err) {
        console.error('Error loading mail stats:', err);
      }
    };
    loadMailStats();
  }, []);

  React.useEffect(() => {
    const loadQuickStats = async () => {
      try {
        const [casesRes, triageRes, invoicesRes] = await Promise.all([
          api.get('/cases/').catch(() => ({ data: [] })),
          api.get('/triage/stats').catch(() => ({ data: {} })),
          api.get('/invoices/stats').catch(() => ({ data: {} }))
        ]);

        // Cases endpoint returns an array directly
        const activeCases = Array.isArray(casesRes.data)
          ? casesRes.data.filter(c => c.status === 'active' || c.status === 'Active').length
          : 0;

        setQuickStats({
          openCases: activeCases,
          pendingTasks: triageRes.data?.pending || 0,
          overdueTasks: triageRes.data?.overdue || 0,
          overdueInvoices: invoicesRes.data?.overdue_invoices || 0,
          loading: false
        });
      } catch (err) {
        console.error('Error loading quick stats:', err);
        setQuickStats(prev => ({ ...prev, loading: false }));
      }
    };

    if (isStaff || isAdmin) {
      loadQuickStats();
    } else {
      setQuickStats(prev => ({ ...prev, loading: false }));
    }
  }, [isStaff, isAdmin]);

  // iOS-style color gradients for each module
  const moduleColors = {
    'documents': 'from-blue-500 to-blue-600',
    'mail': 'from-sky-400 to-blue-500',
    'calendar': 'from-red-500 to-red-600',
    'sigil': 'from-green-500 to-emerald-600',
    'visage': 'from-emerald-400 to-green-500',
    'cases': 'from-amber-500 to-orange-600',
    'contacts': 'from-slate-400 to-slate-500',
    'invoices': 'from-emerald-500 to-teal-600',
    'accounting': 'from-green-600 to-emerald-700',
    'payroll': 'from-violet-500 to-purple-600',
    'triage': 'from-purple-500 to-violet-600',
    'intake': 'from-cyan-500 to-blue-600',
    'dictate': 'from-pink-500 to-rose-600',
    'voip': 'from-green-500 to-green-600',
    'tickets': 'from-orange-500 to-amber-600',
    'law-library': 'from-amber-600 to-yellow-700',
    'profile': 'from-indigo-500 to-blue-600',
    'security': 'from-slate-500 to-slate-600',
    'settings': 'from-gray-500 to-slate-600',
    'conflict-check': 'from-rose-500 to-red-600',
    'admin': 'from-red-600 to-rose-700',
    'audit': 'from-indigo-600 to-purple-700',
    'planchette-intel': 'from-cyan-600 to-teal-700',
  };

  const modules = [
    { id: 'documents', label: 'Documents', icon: FolderOpen, path: '/documents', clientVisible: true },
    { id: 'mail', label: 'OuijaMail', icon: Mail, path: '/mail', staffOnly: true, badge: (emailUnread?.unread_count || 0) > 0 ? emailUnread.unread_count : null },
    { id: 'calendar', label: 'Calendar', icon: Calendar, path: '/calendar', staffOnly: true },
    { id: 'sigil', label: 'Sigil', icon: MessageSquare, path: '/sigil', clientVisible: true, badge: sigilUnreadCount > 0 ? sigilUnreadCount : null },
    { id: 'visage', label: 'Visage', icon: Video, path: '/visage', clientVisible: true },
    { id: 'cases', label: 'Cases', icon: Briefcase, path: '/cases', clientVisible: true },
    { id: 'contacts', label: 'Contacts', icon: UserCircle, path: '/contacts', staffOnly: true },
    { id: 'invoices', label: 'Invoices', icon: Receipt, path: '/invoices', clientVisible: true },
    { id: 'accounting', label: 'Accounting', icon: DollarSign, path: '/accounting', adminOnly: true },
    { id: 'triage', label: 'Triage', icon: Filter, path: '/triage', staffOnly: true },
    { id: 'intake', label: 'Intake', icon: UserPlus, path: '/intake', staffOnly: true },
    { id: 'dictate', label: 'Dictate', icon: Mic, path: '/dictate', staffOnly: true },
    { id: 'voip', label: 'Phone', icon: Phone, path: '/voip', staffOnly: true },
    { id: 'tickets', label: 'Tickets', icon: Ticket, path: '/tickets', staffOnly: true },
    { id: 'law-library', label: 'Law Library', icon: BookOpen, path: '/law-library', staffOnly: true },
    { id: 'planchette-intel', label: 'PlanchetteIntel', icon: Search, path: '/planchette-intel', staffOnly: true },
    { id: 'profile', label: 'Profile', icon: User, path: '/profile', clientVisible: true },
    { id: 'security', label: 'Security', icon: Lock, path: '/security', clientVisible: true },
    { id: 'settings', label: 'Settings', icon: SettingsIcon, path: '/settings', staffOnly: true }
  ];

  const adminModules = [
    { id: 'admin', label: 'Admin', icon: Shield, path: '/admin', adminOnly: true },
    { id: 'audit', label: 'Audit Logs', icon: Activity, path: '/audit', adminOnly: true },
  ];

  const filterModules = (moduleList) => {
    return moduleList.filter(module => {
      if (isAdmin) return true;
      if (module.adminOnly) return false;
      if (isStaff) return true;
      if (module.staffOnly) return false;
      return module.clientVisible || (!module.staffOnly && !module.adminOnly);
    });
  };

  const visibleModules = filterModules(modules);
  const visibleAdminModules = isAdmin ? adminModules : [];
  const allModules = [...visibleModules, ...visibleAdminModules];

  const handleModuleClick = (module) => {
    navigate(module.path);
  };

  const handleDragStart = (e, moduleId) => {
    setDraggedModule(moduleId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetModuleId) => {
    e.preventDefault();
    if (!draggedModule || draggedModule === targetModuleId) return;

    const currentOrder = moduleOrder || allModules.map(m => m.id);
    const draggedIndex = currentOrder.indexOf(draggedModule);
    const targetIndex = currentOrder.indexOf(targetModuleId);

    const newOrder = [...currentOrder];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedModule);

    setModuleOrder(newOrder);
    localStorage.setItem('dashboardModuleOrder', JSON.stringify(newOrder));
    setDraggedModule(null);
  };

  const handleDragEnd = () => {
    setDraggedModule(null);
  };

  // Sort modules by saved order
  const getSortedModules = () => {
    if (!moduleOrder) return allModules;
    return [...allModules].sort((a, b) => {
      const aIndex = moduleOrder.indexOf(a.id);
      const bIndex = moduleOrder.indexOf(b.id);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  };

  const sortedModules = getSortedModules();

  return (
    <div className="relative min-h-screen">
      <SpaceBackground />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Welcome Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                <Sparkles className="text-white" size={28} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">
                  Welcome back, {user?.display_name || user?.full_name || user?.username}!
                </h1>
                <p className="text-slate-400 text-sm">
                  Your secure legal practice management platform
                </p>
              </div>
            </div>
            <div className="hidden md:block">
              <WeatherWidget />
            </div>
          </div>
        </div>

        {/* While You Were Gone - Activity Summary */}
        {isFirmMember && <WhileYouWereGoneWidget />}
        {/* eService Filings Queue - surface unmatched filings before links expire */}
        {/* Case Alerts */}
        {/* Morning Briefing — top card */}
        {(isStaff || isAdmin) && <MorningBriefing />}
        {(isStaff || isAdmin) && <AlertCenter />}
        {(isStaff || isAdmin) && <EserviceFilingsWidget />}
        {(isStaff || isAdmin) && <SupportTicketsWidget />}

        {/* Command Center - Events, Triage, Billable Hours & Approvals */}
        {(isStaff || isAdmin) && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-8">
            <TodaysEventsWidget />
            <TriageWidget />
            {(isAttorney || isAdmin) ? <ApprovalWidget /> : <BillableHoursWidget />}
            {(isAttorney || isAdmin) && <CallbacksCard />}
            <PlanchetteIntelWidget />
          </div>
        )}

        {/* Show only events for clients */}
        {!isStaff && !isAdmin && (
          <div className="mb-8">
            <TodaysEventsWidget />
          </div>
        )}

        {/* Desktop-style Icon Grid */}
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-6 mb-8">
          {sortedModules.map((module) => {
            const Icon = module.icon;
            return (
              <div
                key={module.id}
                draggable
                onDragStart={(e) => handleDragStart(e, module.id)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, module.id)}
                onDragEnd={handleDragEnd}
                onClick={() => handleModuleClick(module)}
                className={`group flex flex-col items-center gap-3 p-4 rounded-2xl transition-all duration-200 hover:bg-white/5 active:scale-95 relative cursor-pointer select-none ${
                  draggedModule === module.id ? 'opacity-50 scale-95' : ''
                }`}
                title={module.description}
              >
                {/* Icon Container */}
                <div className="relative">
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${moduleColors[module.id] || "from-blue-500 to-blue-600"} shadow-lg flex items-center justify-center transform transition-all duration-200 group-hover:scale-110 group-hover:shadow-xl`}>
                    <Icon className="text-white" size={36} strokeWidth={1.5} />
                  </div>
                  {module.badge && (
                    <span className="absolute -top-2.5 -right-2.5 bg-red-500 text-white text-base font-bold rounded-full h-8 min-w-[32px] px-2 flex items-center justify-center shadow-lg border-2 border-slate-900 ring-2 ring-red-500/20">
                      {module.badge > 99 ? "99+" : module.badge}
                    </span>
                  )}
                </div>

                {/* Label */}
                <span className="text-base font-semibold text-slate-300 text-center group-hover:text-white transition-colors drop-shadow-sm">
                  {module.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Quick Stats */}
        {(isStaff || isAdmin) && (
          <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-5">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <TrendingUp size={18} className="text-blue-400" />
              Quick Stats
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <button
                onClick={() => navigate('/cases')}
                className="flex flex-col p-4 bg-blue-500/10 rounded-xl border border-blue-500/20 hover:border-blue-400/40 hover:bg-blue-500/20 transition-all group"
              >
                <div className="flex items-center justify-between mb-2">
                  <Briefcase size={18} className="text-blue-400" />
                  <span className="text-2xl font-bold text-white">
                    {quickStats.loading ? '...' : quickStats.openCases}
                  </span>
                </div>
                <span className="text-sm text-blue-300">Active Cases</span>
              </button>

              <button
                onClick={() => navigate('/triage')}
                className="flex flex-col p-4 bg-amber-500/10 rounded-xl border border-amber-500/20 hover:border-amber-400/40 hover:bg-amber-500/20 transition-all group"
              >
                <div className="flex items-center justify-between mb-2">
                  <ClipboardList size={18} className="text-amber-400" />
                  <span className="text-2xl font-bold text-white">
                    {quickStats.loading ? '...' : quickStats.pendingTasks}
                  </span>
                </div>
                <span className="text-sm text-amber-300">Pending Tasks</span>
              </button>

              <button
                onClick={() => navigate('/triage')}
                className="flex flex-col p-4 bg-red-500/10 rounded-xl border border-red-500/20 hover:border-red-400/40 hover:bg-red-500/20 transition-all group"
              >
                <div className="flex items-center justify-between mb-2">
                  <AlertCircle size={18} className="text-red-400" />
                  <span className="text-2xl font-bold text-white">
                    {quickStats.loading ? '...' : quickStats.overdueTasks}
                  </span>
                </div>
                <span className="text-sm text-red-300">Overdue Tasks</span>
              </button>

              <button
                onClick={() => navigate('/invoices?status=overdue')}
                className="flex flex-col p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/20 hover:border-emerald-400/40 hover:bg-emerald-500/20 transition-all group"
              >
                <div className="flex items-center justify-between mb-2">
                  <Receipt size={18} className="text-emerald-400" />
                  <span className="text-2xl font-bold text-white">
                    {quickStats.loading ? '...' : quickStats.overdueInvoices}
                  </span>
                </div>
                <span className="text-sm text-emerald-300">Overdue Invoices</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
